import { useEffect, type RefObject } from 'react';

/**
 * Make a muted guide clip autoplay INLINE on iOS Safari, which is fussy:
 * - it plays muted inline video only when the `muted` ATTRIBUTE is present, but
 *   React sets only the muted *property* — so we set the attribute on the element;
 * - `.play()` must be (re)tried once the media is ready and when the element scrolls
 *   into view (iOS won't autoplay off-screen video, and the guide page stacks several).
 *
 * Pass the themed src as `dep` so it re-arms when the source swaps (light/dark).
 */
export function useInlineAutoplay(ref: RefObject<HTMLVideoElement | null>, dep: string): void {
  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    v.muted = true;
    v.defaultMuted = true;
    v.setAttribute('muted', '');
    v.playsInline = true;
    v.setAttribute('playsinline', '');
    v.setAttribute('webkit-playsinline', '');

    let inView = true; // assume in view until the observer says otherwise
    const tryPlay = () => {
      if (!inView) return;
      const p = v.play();
      if (p) p.catch(() => { /* iOS may reject until in-view; the observer retries */ });
    };
    tryPlay();
    v.addEventListener('canplay', tryPlay);
    v.addEventListener('loadeddata', tryPlay);

    // Scrollplay: only on-screen clips play. iOS Safari caps how many videos may
    // play at once, so pausing off-screen clips frees slots and lets the visible
    // one(s) autoplay reliably — otherwise some clips never start on an article
    // page that stacks several.
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        inView = e.isIntersecting;
        if (inView) tryPlay();
        else v.pause();
      }),
      { threshold: 0.25 },
    );
    io.observe(v);

    return () => {
      v.removeEventListener('canplay', tryPlay);
      v.removeEventListener('loadeddata', tryPlay);
      io.disconnect();
    };
  }, [ref, dep]);
}
